import { useCallback, useState } from 'react'
import {
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Spinner,
  Typography,
} from '@material-tailwind/react'
import { Trans, useTranslation } from 'react-i18next'
import { colors } from '@material-tailwind/react/types/generic'

import { useGeneralToast } from '../hooks/toast'
import { useWindowSize } from '../hooks/toolkit'
import { rejectProposalGroup } from '../utils/helpers/proposal.helpers'
import { useDarkMode } from '../contexts/DarkModeContext'
import { AgamiSpot } from '../types/supabase'

interface RejectProposalDialogProps {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  /** The representative of the group to reject: `proposal.spotIds` carries the whole group. */
  proposalToReject: AgamiSpot | null
  refetchAgamiList: () => Promise<void>
}

/**
 * The confirmation of a rejection (05.4-T5) — ONE for the whole group, not one
 * per row: with the duplicates collapsed (T8), rejecting twelve "musica" is one
 * action with one dialog, which is what keeps the confirmation sustainable on
 * an irreversible action.
 *
 * The text says ELIMINATED, because it is (AC-7): this is the only place in the
 * product where an interface action deletes rows for good — everywhere else
 * deletion is `is_canceled = true`. No temporary undo: with a hard delete there
 * is nothing to undo, and faking one would mean delaying the deletion into a
 * state no other part of the system knows.
 *
 * On partial failure (seven out of twelve, then an error) the message says so
 * instead of declaring success: the failed ones are still pending, retrying is
 * safe.
 */
export const RejectProposalDialog: React.FC<RejectProposalDialogProps> = ({
  isOpen,
  setIsOpen,
  proposalToReject,
  refetchAgamiList,
}) => {
  const { t } = useTranslation()
  const { darkMode } = useDarkMode()
  const { success: toastSuccess, error: toastError } = useGeneralToast()
  const [rejecting, setRejecting] = useState(false)
  const { width } = useWindowSize()
  const dialogSize = (width ?? 1024) <= 640 ? 'lg' : 'xs'

  const count = proposalToReject?.proposal?.count ?? 1

  const reject = useCallback(async () => {
    if (!proposalToReject) return

    const spotIds = proposalToReject.proposal?.spotIds ?? [proposalToReject.id]

    setRejecting(true)

    const { total, failed } = await rejectProposalGroup(spotIds)

    // Refetch in every case: the successful part of a partial failure is gone
    // from the database, and the list must say so.
    await refetchAgamiList()

    if (failed > 0) {
      toastError(t('proposals.rejectDialog.partialFailure', { failed, total }))
    } else {
      toastSuccess(t('proposals.rejectDialog.success', { count: total }))
    }

    setRejecting(false)
    setIsOpen(false)
  }, [proposalToReject, refetchAgamiList, toastError, toastSuccess, t, setIsOpen])

  return (
    <Dialog size={dialogSize} open={isOpen} handler={setIsOpen} data-testid="reject-proposal-dialog">
      <DialogHeader>{t('proposals.rejectDialog.title', { count })}</DialogHeader>
      <DialogBody>
        <Typography className="my-2">
          <Trans
            i18nKey="proposals.rejectDialog.warning"
            count={count}
            values={{ title: proposalToReject?.name }}
            components={{ strong: <strong className="font-extrabold" /> }}
          />
        </Typography>
      </DialogBody>
      <DialogFooter className="justify-between gap-2">
        <Button
          size="sm"
          color={darkMode ? 'white' : ('primary' as colors)}
          variant="outlined"
          onClick={() => setIsOpen(false)}
          disabled={rejecting}
        >
          {t('proposals.rejectDialog.cancel')}
        </Button>
        <Button
          size="sm"
          color="red"
          onClick={reject}
          disabled={rejecting}
          data-testid="reject-proposal-confirm-btn"
        >
          {rejecting ? <Spinner className="h-4 w-4" /> : t('proposals.rejectDialog.confirm', { count })}
        </Button>
      </DialogFooter>
    </Dialog>
  )
}
